import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, ArrowRight, Linkedin, Facebook, Link2, Check } from 'lucide-react';
import '@/styles/boost.css';
import '@/styles/journal.css';
import Starfield from '@/components/Starfield';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { BackToTop } from '@/components/BackToTop';
import Markdown from '@/components/Markdown';
import { useSeo } from '@/lib/seo';
import { PostCard, fmtDate } from './JournalIndex';

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://boostmarketing.agency';
const tagSlug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-');

function ArticleSeo({ post }) {
  const url = `${SITE}/journal/${post.slug}`;
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      '@id': `${url}#article`,
      headline: post.seo_title || post.title,
      name: post.title,
      description: post.seo_description || post.excerpt,
      image: post.cover_image?.startsWith('http') ? post.cover_image : `${SITE}${post.cover_image || '/og-home.png'}`,
      url,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      datePublished: post.published_at,
      dateModified: post.updated_at || post.published_at,
      inLanguage: 'en-GB',
      keywords: (post.keywords || []).concat(post.tags || []).join(', '),
      articleSection: (post.tags || [])[0] || 'Marketing',
      wordCount: (post.body_md || '').split(/\s+/).length,
      author: {
        '@type': 'Organization',
        name: 'BOOST! Design & Marketing',
        url: SITE,
      },
      publisher: {
        '@type': 'Organization',
        name: 'BOOST! Design & Marketing',
        url: SITE,
        logo: { '@type': 'ImageObject', url: `${SITE}/og-home.png` },
        address: {
          '@type': 'PostalAddress',
          streetAddress: '27 Fairycroft Road',
          addressLocality: 'Saffron Walden',
          addressRegion: 'Essex',
          postalCode: 'CB10 1LZ',
          addressCountry: 'GB',
        },
        telephone: '+447909970556',
      },
      isPartOf: { '@type': 'Blog', '@id': `${SITE}/journal#blog` },
      ...(post.locations?.length
        ? { spatialCoverage: post.locations.map((l) => ({ '@type': 'Place', name: l })) }
        : {}),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Journal', item: `${SITE}/journal` },
        { '@type': 'ListItem', position: 3, name: post.title, item: url },
      ],
    },
  ];
  if (post.faqs?.length) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: post.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }
  useSeo({
    title: post.seo_title || `${post.title} | The Boost! Journal`,
    description: post.seo_description || post.excerpt,
    canonical: url,
    image: post.cover_image?.startsWith('http') ? post.cover_image : `${SITE}${post.cover_image || '/og-home.png'}`,
    jsonLd,
  });
  return null;
}

function ShareBar({ post }) {
  const [copied, setCopied] = useState(false);
  const url = `${SITE}/journal/${post.slug}`;
  const enc = encodeURIComponent(url);
  const encTitle = encodeURIComponent(post.title);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard unavailable */ }
  };
  return (
    <div className="jr-share" data-testid="journal-share">
      <span className="jr-share-label">Share this article</span>
      <div className="jr-share-btns">
        <a
          className="jr-share-btn jr-share-li"
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc}`}
          target="_blank" rel="noopener noreferrer"
          aria-label="Share on LinkedIn" data-testid="journal-share-linkedin"
        >
          <Linkedin size={16} /> LinkedIn
        </a>
        <a
          className="jr-share-btn jr-share-fb"
          href={`https://www.facebook.com/sharer/sharer.php?u=${enc}`}
          target="_blank" rel="noopener noreferrer"
          aria-label="Share on Facebook" data-testid="journal-share-facebook"
        >
          <Facebook size={16} /> Facebook
        </a>
        <button
          className="jr-share-btn jr-share-copy"
          onClick={copy}
          aria-label="Copy link" data-testid="journal-share-copy"
        >
          {copied ? <><Check size={16} /> Copied</> : <><Link2 size={16} /> Copy link</>}
        </button>
      </div>
    </div>
  );
}

function Article({ post, related }) {
  return (
    <>
      <ArticleSeo post={post} />
      <main>
        <article className="jr-article">
          <div className="jr-article-inner">
            <Link to="/journal" className="jr-back" data-testid="journal-back">
              <ArrowLeft size={15} /> The Journal
            </Link>
            <h1 className="jr-article-h1" data-testid="journal-post-title">{post.title}</h1>
            <div className="jr-article-meta">
              <span><Calendar size={14} /> {fmtDate(post.published_at)}</span>
              <span><Clock size={14} /> {post.reading_minutes} min read</span>
              <span><User size={14} /> {post.author}</span>
            </div>
            <div className="jr-tags" style={{ marginBottom: 30 }}>
              {(post.tags || []).map((t) => (
                <Link key={t} to={`/journal/tag/${tagSlug(t)}`} className="jr-tag" data-testid={`journal-post-tag-${tagSlug(t)}`}>
                  <Tag size={12} /> {t}
                </Link>
              ))}
            </div>
            {post.cover_image && (
              <img className="jr-cover" src={post.cover_image} alt={post.cover_alt || post.title} data-testid="journal-post-cover" />
            )}
            {post.excerpt && <p className="jr-lead">{post.excerpt}</p>}

            <Markdown testId="journal-post-body">{post.body_md}</Markdown>

            <ShareBar post={post} />

            {post.faqs?.length > 0 && (
              <div className="jr-faqs" data-testid="journal-post-faqs">
                <h2 className="jr-body" style={{ fontFamily: 'var(--font-display)', fontSize: 30, color: '#fff', margin: '0 0 18px' }}>
                  Frequently asked
                </h2>
                {post.faqs.map((f, i) => (
                  <details className="jr-faq" key={i} data-testid={`journal-faq-${i}`}>
                    <summary>{f.q}</summary>
                    <p>{f.a}</p>
                  </details>
                ))}
              </div>
            )}

            {post.case_study_url && (
              <div className="jr-cs-cta" data-testid="journal-case-study-cta">
                <p>
                  See the full case study
                  <span>Imagery, timeline and the technical detail behind this story.</span>
                </p>
                <Link to={post.case_study_url} className="boost-btn boost-btn-primary">
                  View case study <ArrowRight size={15} />
                </Link>
              </div>
            )}
          </div>
        </article>

        {related?.length > 0 && (
          <section className="jr-related">
            <div className="jr-related-inner">
              <h2 className="jr-related-title">More mission logs</h2>
              <div className="jr-grid" data-testid="journal-related">
                {related.map((p) => <PostCard key={p.slug} post={p} testId={`journal-related-${p.slug}`} />)}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}

export default function JournalPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let live = true;
    setData(null);
    setNotFound(false);
    fetch(`${API}/api/blog/posts/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => live && setData(d))
      .catch(() => live && setNotFound(true));
    return () => { live = false; };
  }, [slug]);

  return (
    <div className="boost-app" data-testid="journal-post">
      <Starfield />
      <Navbar />
      {data && <Article post={data.post} related={data.related} />}
      {!data && (
        <main className="jr-article">
          <div className="jr-article-inner">
            {notFound ? (
              <>
                <p className="jr-empty" data-testid="journal-post-notfound">
                  That article has moved or never launched. Head back to the Journal for the full archive.
                </p>
                <button className="boost-btn boost-btn-primary" onClick={() => navigate('/journal')}>
                  <ArrowLeft size={15} /> Back to the Journal
                </button>
              </>
            ) : (
              <p className="jr-empty">Loading article…</p>
            )}
          </div>
        </main>
      )}
      <Footer />
      <BackToTop />
    </div>
  );
}
